import { PanelLeft, PanelRight } from "lucide-react";
import {
  type CSSProperties,
  type HTMLAttributes,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";

import { cn } from "../layout";
import { Sheet, SheetContent, SheetTitle } from "../primitives";
import {
  type AgentWorkbenchAppearanceOptions,
  useAgentWorkbenchAppearance,
} from "./appearance";

export type AgentWorkbenchShellLayout =
  | "wide"
  | "compact"
  | "drawer"
  | "mobile";

export type AgentWorkbenchShellMeasurement = "container" | "viewport";

export interface AgentWorkbenchShellBreakpoints {
  mobile: number;
  drawer: number;
  compact: number;
  lowHeight: number;
}

export const AGENT_WORKBENCH_SHELL_BREAKPOINTS: AgentWorkbenchShellBreakpoints =
  Object.freeze({
    mobile: 768,
    drawer: 1200,
    compact: 1440,
    lowHeight: 768,
  });

export function resolveAgentWorkbenchShellLayout(
  width: number,
  breakpoints: AgentWorkbenchShellBreakpoints = AGENT_WORKBENCH_SHELL_BREAKPOINTS,
): AgentWorkbenchShellLayout {
  if (width < breakpoints.mobile) return "mobile";
  if (width < breakpoints.drawer) return "drawer";
  if (width < breakpoints.compact) return "compact";
  return "wide";
}

export interface AgentWorkbenchShellClassNames {
  root?: string;
  controls?: string;
  sidebar?: string;
  main?: string;
  details?: string;
  sidebarOverlay?: string;
  detailsOverlay?: string;
  resizeHandle?: string;
}

export interface AgentWorkbenchShellLabels {
  openSidebar?: string;
  sidebar?: string;
  openDetails?: string;
  details?: string;
  resizeSidebar?: string;
  resizeDetails?: string;
}

export interface AgentWorkbenchShellProps
  extends Omit<HTMLAttributes<HTMLDivElement>, "children">,
    AgentWorkbenchAppearanceOptions {
  /** Left navigation content. The shell only owns its layout and overflow boundary. */
  sidebar?: ReactNode;
  /** Primary conversation content. */
  children: ReactNode;
  /** Optional right-side task details or artifact workspace. */
  details?: ReactNode;
  /** Whether the left navigation is available to the layout. */
  sidebarOpen?: boolean;
  /** Whether the left navigation starts expanded in inline layouts. */
  sidebarDefaultOpen?: boolean;
  /** Whether the right details region is available to the layout. */
  detailsOpen?: boolean;
  /** Notified when a responsive details overlay closes. */
  onDetailsOpenChange?: (open: boolean) => void;
  /** Width of the left navigation column in CSS pixels or any valid CSS size. */
  sidebarWidth?: number | string;
  /** Width of the right details column in CSS pixels or any valid CSS size. */
  detailsWidth?: number | string;
  sidebarMinWidth?: number;
  sidebarMaxWidth?: number;
  detailsMinWidth?: number;
  detailsMaxWidth?: number;
  mainMinWidth?: number;
  /** Enables the canonical wide/compact/drawer/mobile layout policy. */
  responsive?: boolean;
  /** Container measurement is reusable; viewport keeps nested workbench shells in sync. */
  responsiveMeasurement?: AgentWorkbenchShellMeasurement;
  /** Explicit dimensions are useful for deterministic embeds and visual tests. */
  responsiveWidth?: number;
  responsiveHeight?: number;
  breakpoints?: AgentWorkbenchShellBreakpoints;
  onLayoutChange?: (layout: AgentWorkbenchShellLayout) => void;
  /** Shows the canonical navigation button whenever the sidebar becomes an overlay. */
  showResponsiveControls?: boolean;
  /** Shells with the same group id keep responsive overlays mutually exclusive. */
  overlayGroupId?: string;
  /** Enables pointer and keyboard resizing for inline side regions. */
  resizable?: boolean;
  widthStorageKey?: string;
  onSidebarWidthChange?: (width: number) => void;
  onDetailsWidthChange?: (width: number) => void;
  labels?: AgentWorkbenchShellLabels;
  classNames?: AgentWorkbenchShellClassNames;
}

type ShellStyle = CSSProperties & {
  "--agent-workbench-sidebar-width"?: string;
  "--agent-workbench-details-width"?: string;
};

type MeasuredSize = { width: number; height: number };
type ResizeRegion = "sidebar" | "details";

const OVERLAY_EVENT = "monkeys-agent-workbench-overlay-open";

function cssSize(value: number | string): string {
  return typeof value === "number" ? `${value}px` : value;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), Math.max(min, max));
}

function initialMeasuredSize(): MeasuredSize {
  if (typeof window === "undefined") return { width: 1440, height: 900 };
  return {
    width: window.visualViewport?.width ?? window.innerWidth,
    height: window.visualViewport?.height ?? window.innerHeight,
  };
}

function readStoredWidths(
  storageKey?: string,
): Partial<Record<ResizeRegion, number>> {
  if (!storageKey || typeof window === "undefined") return {};
  try {
    const value = JSON.parse(window.localStorage.getItem(storageKey) ?? "{}");
    return typeof value === "object" && value !== null ? value : {};
  } catch {
    return {};
  }
}

function writeStoredWidths(
  storageKey: string | undefined,
  widths: Record<ResizeRegion, number>,
) {
  if (!storageKey || typeof window === "undefined") return;
  window.localStorage.setItem(storageKey, JSON.stringify(widths));
}

/**
 * Canonical responsive shell for Agent workbenches.
 *
 * - >=1440px: complete three-column layout.
 * - 1200-1439px: compact navigation + main; details use an overlay.
 * - 768-1199px: main-only layout with mutually exclusive drawers.
 * - <768px: main-only layout with full-screen side panels.
 *
 * Every region remains bounded by one scroll owner. Opening or closing an
 * overlay never reparents the main conversation, so its scroll and runtime
 * state remain intact.
 */
export function AgentWorkbenchShell({
  sidebar,
  children,
  details,
  sidebarOpen = true,
  sidebarDefaultOpen = true,
  detailsOpen = true,
  onDetailsOpenChange,
  sidebarWidth = 280,
  detailsWidth = 360,
  sidebarMinWidth = 240,
  sidebarMaxWidth = 400,
  detailsMinWidth = 320,
  detailsMaxWidth = 720,
  mainMinWidth = 600,
  responsive = false,
  responsiveMeasurement = "container",
  responsiveWidth,
  responsiveHeight,
  breakpoints = AGENT_WORKBENCH_SHELL_BREAKPOINTS,
  onLayoutChange,
  showResponsiveControls = true,
  overlayGroupId,
  resizable = false,
  widthStorageKey,
  onSidebarWidthChange,
  onDetailsWidthChange,
  labels,
  variant,
  density,
  className,
  classNames,
  style,
  ...rootProps
}: AgentWorkbenchShellProps) {
  const appearance = useAgentWorkbenchAppearance({ variant, density });
  const rootRef = useRef<HTMLDivElement>(null);
  const sidebarReturnFocusRef = useRef<HTMLElement | null>(null);
  const detailsReturnFocusRef = useRef<HTMLElement | null>(null);
  const shellId = useId();
  const [measuredSize, setMeasuredSize] = useState(initialMeasuredSize);
  const storedWidths = useMemo(
    () => readStoredWidths(widthStorageKey),
    [widthStorageKey],
  );
  const [sidebarPixelWidth, setSidebarPixelWidth] = useState(() =>
    typeof sidebarWidth === "number"
      ? storedWidths.sidebar ?? sidebarWidth
      : 280,
  );
  const [detailsPixelWidth, setDetailsPixelWidth] = useState(() =>
    typeof detailsWidth === "number"
      ? storedWidths.details ?? detailsWidth
      : 360,
  );
  const [sidebarOverlayOpen, setSidebarOverlayOpen] = useState(false);
  const [detailsOverlayOpen, setDetailsOverlayOpen] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(sidebarDefaultOpen);

  const rememberReturnFocus = useCallback(
    (region: ResizeRegion, candidate: EventTarget | null = document.activeElement) => {
      if (
        !(candidate instanceof HTMLElement) ||
        candidate === document.body ||
        candidate === document.documentElement
      ) {
        return;
      }
      const targetRef =
        region === "sidebar" ? sidebarReturnFocusRef : detailsReturnFocusRef;
      targetRef.current = candidate;
    },
    [],
  );

  const restoreReturnFocus = useCallback((region: ResizeRegion) => {
    const targetRef =
      region === "sidebar" ? sidebarReturnFocusRef : detailsReturnFocusRef;
    const target = targetRef.current;
    targetRef.current = null;
    if (!target?.isConnected) return;
    window.requestAnimationFrame(() => {
      if (target.isConnected) target.focus();
    });
  }, []);

  useEffect(() => {
    if (typeof sidebarWidth === "number") {
      setSidebarPixelWidth(storedWidths.sidebar ?? sidebarWidth);
    }
  }, [sidebarWidth, storedWidths.sidebar]);

  useEffect(() => setSidebarExpanded(sidebarDefaultOpen), [sidebarDefaultOpen]);

  useEffect(() => {
    if (typeof detailsWidth === "number") {
      setDetailsPixelWidth(storedWidths.details ?? detailsWidth);
    }
  }, [detailsWidth, storedWidths.details]);

  useEffect(() => {
    if (!responsive) return undefined;

    const updateViewport = () => {
      const viewport = initialMeasuredSize();
      setMeasuredSize({
        width: responsiveWidth ?? viewport.width,
        height: responsiveHeight ?? viewport.height,
      });
    };

    if (
      responsiveMeasurement === "viewport" ||
      responsiveWidth !== undefined ||
      responsiveHeight !== undefined
    ) {
      updateViewport();
      if (responsiveWidth !== undefined && responsiveHeight !== undefined) {
        return undefined;
      }
      window.addEventListener("resize", updateViewport);
      window.visualViewport?.addEventListener("resize", updateViewport);
      return () => {
        window.removeEventListener("resize", updateViewport);
        window.visualViewport?.removeEventListener("resize", updateViewport);
      };
    }

    const root = rootRef.current;
    if (!root) return undefined;
    const updateContainer = () => {
      const rect = root.getBoundingClientRect();
      setMeasuredSize({ width: rect.width, height: rect.height });
    };
    updateContainer();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateContainer);
      return () => window.removeEventListener("resize", updateContainer);
    }
    const observer = new ResizeObserver(updateContainer);
    observer.observe(root);
    return () => observer.disconnect();
  }, [responsive, responsiveHeight, responsiveMeasurement, responsiveWidth]);

  const layout = responsive
    ? resolveAgentWorkbenchShellLayout(
        responsiveWidth ?? measuredSize.width,
        breakpoints,
      )
    : "wide";
  const lowHeight =
    responsive &&
    (responsiveHeight ?? measuredSize.height) <= breakpoints.lowHeight;

  useEffect(() => onLayoutChange?.(layout), [layout, onLayoutChange]);

  const hasSidebar = sidebar !== undefined && sidebarOpen;
  const hasDetails = details !== undefined && detailsOpen;
  const sidebarUsesOverlay = layout === "drawer" || layout === "mobile";
  const inlineSidebar =
    hasSidebar && sidebarExpanded && !sidebarUsesOverlay;
  const inlineDetails = hasDetails && layout === "wide";

  const resolvedSidebarMax = Math.max(
    sidebarMinWidth,
    Math.min(
      sidebarMaxWidth,
      layout === "compact" ? 320 : sidebarMaxWidth,
      measuredSize.width -
        (inlineDetails ? detailsPixelWidth : 0) -
        mainMinWidth,
    ),
  );
  const resolvedDetailsMax = Math.max(
    detailsMinWidth,
    Math.min(
      detailsMaxWidth,
      measuredSize.width -
        (inlineSidebar ? sidebarPixelWidth : 0) -
        mainMinWidth,
    ),
  );
  const resolvedSidebarWidth =
    typeof sidebarWidth === "number"
      ? clamp(sidebarPixelWidth, sidebarMinWidth, resolvedSidebarMax)
      : sidebarWidth;
  const resolvedDetailsWidth =
    typeof detailsWidth === "number"
      ? clamp(detailsPixelWidth, detailsMinWidth, resolvedDetailsMax)
      : detailsWidth;

  const requestOverlay = useCallback(
    (region: ResizeRegion) => {
      if (!overlayGroupId || typeof window === "undefined") return;
      window.dispatchEvent(
        new CustomEvent(OVERLAY_EVENT, {
          detail: { groupId: overlayGroupId, ownerId: shellId, region },
        }),
      );
    },
    [overlayGroupId, shellId],
  );

  useEffect(() => {
    if (!overlayGroupId || typeof window === "undefined") return undefined;
    const handleOverlayOpen = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      if (detail?.groupId !== overlayGroupId || detail?.ownerId === shellId)
        return;
      setSidebarOverlayOpen(false);
      setDetailsOverlayOpen((open) => {
        if (open) onDetailsOpenChange?.(false);
        return false;
      });
    };
    window.addEventListener(OVERLAY_EVENT, handleOverlayOpen);
    return () => window.removeEventListener(OVERLAY_EVENT, handleOverlayOpen);
  }, [onDetailsOpenChange, overlayGroupId, shellId]);

  useEffect(() => {
    if (inlineSidebar || !hasSidebar) setSidebarOverlayOpen(false);
  }, [hasSidebar, inlineSidebar]);

  useEffect(() => {
    if (inlineDetails || !hasDetails) {
      setDetailsOverlayOpen(false);
      return;
    }
    rememberReturnFocus("details");
    requestOverlay("details");
    setDetailsOverlayOpen(true);
  }, [hasDetails, inlineDetails, rememberReturnFocus, requestOverlay]);

  const persistWidths = useCallback(
    (sidebarValue: number, detailsValue: number) => {
      writeStoredWidths(widthStorageKey, {
        sidebar: sidebarValue,
        details: detailsValue,
      });
    },
    [widthStorageKey],
  );

  const updateRegionWidth = useCallback(
    (region: ResizeRegion, value: number) => {
      if (region === "sidebar") {
        const next = clamp(value, sidebarMinWidth, resolvedSidebarMax);
        setSidebarPixelWidth(next);
        onSidebarWidthChange?.(next);
        return next;
      }
      const next = clamp(value, detailsMinWidth, resolvedDetailsMax);
      setDetailsPixelWidth(next);
      onDetailsWidthChange?.(next);
      return next;
    },
    [
      detailsMinWidth,
      onDetailsWidthChange,
      onSidebarWidthChange,
      resolvedDetailsMax,
      resolvedSidebarMax,
      sidebarMinWidth,
    ],
  );

  const beginResize = useCallback(
    (region: ResizeRegion, event: ReactPointerEvent<HTMLButtonElement>) => {
      if (!resizable || event.button !== 0) return;
      event.preventDefault();
      const startX = event.clientX;
      const startWidth =
        region === "sidebar" ? sidebarPixelWidth : detailsPixelWidth;
      let latestWidth = startWidth;
      const previousCursor = document.body.style.cursor;
      const previousUserSelect = document.body.style.userSelect;
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";

      const handleMove = (pointerEvent: PointerEvent) => {
        const delta = pointerEvent.clientX - startX;
        latestWidth = updateRegionWidth(
          region,
          startWidth + (region === "sidebar" ? delta : -delta),
        );
      };
      const handleUp = () => {
        document.body.style.cursor = previousCursor;
        document.body.style.userSelect = previousUserSelect;
        window.removeEventListener("pointermove", handleMove);
        window.removeEventListener("pointerup", handleUp);
        persistWidths(
          region === "sidebar" ? latestWidth : sidebarPixelWidth,
          region === "details" ? latestWidth : detailsPixelWidth,
        );
      };
      window.addEventListener("pointermove", handleMove);
      window.addEventListener("pointerup", handleUp, { once: true });
    },
    [
      detailsPixelWidth,
      persistWidths,
      resizable,
      sidebarPixelWidth,
      updateRegionWidth,
    ],
  );

  const resizeWithKeyboard = useCallback(
    (region: ResizeRegion, direction: number) => {
      const current =
        region === "sidebar" ? sidebarPixelWidth : detailsPixelWidth;
      const next = updateRegionWidth(region, current + direction * 16);
      persistWidths(
        region === "sidebar" ? next : sidebarPixelWidth,
        region === "details" ? next : detailsPixelWidth,
      );
    },
    [detailsPixelWidth, persistWidths, sidebarPixelWidth, updateRegionWidth],
  );

  const shellStyle: ShellStyle = {
    "--agent-workbench-sidebar-width": inlineSidebar
      ? cssSize(resolvedSidebarWidth)
      : "0px",
    "--agent-workbench-details-width": inlineDetails
      ? cssSize(resolvedDetailsWidth)
      : "0px",
    gridTemplateColumns:
      inlineSidebar && inlineDetails
        ? "var(--agent-workbench-sidebar-width) minmax(0, 1fr) var(--agent-workbench-details-width)"
        : inlineSidebar
          ? "var(--agent-workbench-sidebar-width) minmax(0, 1fr)"
          : inlineDetails
            ? "minmax(0, 1fr) var(--agent-workbench-details-width)"
            : "minmax(0, 1fr)",
    ...style,
  };

  const sidebarOverlay = hasSidebar && sidebarUsesOverlay;
  const sidebarRestoreControl =
    hasSidebar && !sidebarExpanded && !sidebarUsesOverlay;
  const detailsOverlay = hasDetails && !inlineDetails;
  const showControls =
    responsive &&
    showResponsiveControls &&
    (sidebarOverlay || sidebarRestoreControl || detailsOverlay);
  const mobilePanel = layout === "mobile";
  const controlSize = mobilePanel ? "size-11" : "size-9";
  const panelCloseClass = mobilePanel
    ? "[&>button]:flex [&>button]:size-11 [&>button]:items-center [&>button]:justify-center"
    : "";

  return (
    <div
      {...rootProps}
      ref={rootRef}
      role={rootProps.role ?? "region"}
      aria-label={rootProps["aria-label"] ?? "Agent workbench"}
      data-agent-workbench-shell="true"
      data-agent-workbench-variant={appearance.variant}
      data-agent-workbench-density={appearance.density}
      data-agent-workbench-layout={layout}
      data-agent-workbench-low-height={lowHeight ? "true" : "false"}
      data-sidebar-open={hasSidebar ? "true" : "false"}
      data-sidebar-expanded={sidebarExpanded ? "true" : "false"}
      data-sidebar-presentation={
        inlineSidebar ? "inline" : sidebarOverlay ? "overlay" : "hidden"
      }
      data-details-open={hasDetails ? "true" : "false"}
      data-details-presentation={
        inlineDetails ? "inline" : detailsOverlay ? "overlay" : "hidden"
      }
      className={cn(
        "relative grid size-full min-h-0 min-w-0 overflow-hidden bg-background text-foreground",
        classNames?.root,
        className,
      )}
      style={shellStyle}
    >
      {inlineSidebar ? (
        <div
          data-agent-workbench-region="sidebar"
          className={cn(
            "min-h-0 min-w-0 overflow-hidden border-r border-border bg-card text-card-foreground",
            classNames?.sidebar,
          )}
        >
          {sidebar}
        </div>
      ) : null}
      <div
        data-agent-workbench-region="main"
        className={cn(
          "flex min-h-0 min-w-0 flex-col overflow-hidden bg-background",
          classNames?.main,
        )}
      >
        {showControls ? (
          <div
            data-agent-workbench-responsive-controls="true"
            className={cn(
              "flex min-h-11 shrink-0 items-center gap-1 border-b border-border bg-background px-2",
              lowHeight && "min-h-10",
              classNames?.controls,
            )}
          >
            {sidebarOverlay || sidebarRestoreControl ? (
              <button
                type="button"
                className={cn(
                  "inline-flex items-center justify-center rounded-[var(--radius)] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  controlSize,
                )}
                aria-label={labels?.openSidebar ?? "Open navigation"}
                aria-expanded={
                  sidebarRestoreControl ? sidebarExpanded : sidebarOverlayOpen
                }
                onClick={(event) => {
                  if (sidebarRestoreControl) {
                    setSidebarExpanded(true);
                    return;
                  }
                  rememberReturnFocus("sidebar", event.currentTarget);
                  requestOverlay("sidebar");
                  setSidebarOverlayOpen(true);
                }}
              >
                <PanelLeft className="size-5" aria-hidden="true" />
              </button>
            ) : null}
            {detailsOverlay && details ? (
              <button
                type="button"
                className={cn(
                  "ml-auto inline-flex items-center justify-center rounded-[var(--radius)] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  controlSize,
                )}
                aria-label={labels?.openDetails ?? "Open details"}
                aria-expanded={detailsOverlayOpen}
                onClick={(event) => {
                  rememberReturnFocus("details", event.currentTarget);
                  requestOverlay("details");
                  setDetailsOverlayOpen(true);
                }}
              >
                <PanelRight className="size-5" aria-hidden="true" />
              </button>
            ) : null}
          </div>
        ) : null}
        <div className="min-h-0 min-w-0 flex-1 overflow-hidden">{children}</div>
      </div>
      {inlineDetails ? (
        <div
          data-agent-workbench-region="details"
          className={cn(
            "min-h-0 min-w-0 overflow-hidden border-l border-border bg-card text-card-foreground",
            classNames?.details,
          )}
        >
          {details}
        </div>
      ) : null}

      {resizable && inlineSidebar && typeof sidebarWidth === "number" ? (
        <button
          type="button"
          role="separator"
          aria-orientation="vertical"
          aria-label={labels?.resizeSidebar ?? "Resize navigation"}
          aria-valuemin={sidebarMinWidth}
          aria-valuemax={resolvedSidebarMax}
          aria-valuenow={Number(resolvedSidebarWidth)}
          className={cn(
            "absolute inset-y-0 z-20 w-2 -translate-x-1/2 cursor-col-resize touch-none bg-transparent outline-none after:absolute after:inset-y-0 after:left-1/2 after:w-px after:-translate-x-1/2 after:bg-border after:transition-colors hover:after:bg-primary focus-visible:after:w-0.5 focus-visible:after:bg-ring",
            classNames?.resizeHandle,
          )}
          style={{ left: "var(--agent-workbench-sidebar-width)" }}
          onPointerDown={(event) => beginResize("sidebar", event)}
          onKeyDown={(event) => {
            if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
            event.preventDefault();
            resizeWithKeyboard("sidebar", event.key === "ArrowRight" ? 1 : -1);
          }}
        />
      ) : null}
      {resizable && inlineDetails && typeof detailsWidth === "number" ? (
        <button
          type="button"
          role="separator"
          aria-orientation="vertical"
          aria-label={labels?.resizeDetails ?? "Resize details"}
          aria-valuemin={detailsMinWidth}
          aria-valuemax={resolvedDetailsMax}
          aria-valuenow={Number(resolvedDetailsWidth)}
          className={cn(
            "absolute inset-y-0 right-[var(--agent-workbench-details-width)] z-20 w-2 translate-x-1/2 cursor-col-resize touch-none bg-transparent outline-none after:absolute after:inset-y-0 after:left-1/2 after:w-px after:-translate-x-1/2 after:bg-border after:transition-colors hover:after:bg-primary focus-visible:after:w-0.5 focus-visible:after:bg-ring",
            classNames?.resizeHandle,
          )}
          onPointerDown={(event) => beginResize("details", event)}
          onKeyDown={(event) => {
            if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
            event.preventDefault();
            resizeWithKeyboard("details", event.key === "ArrowLeft" ? 1 : -1);
          }}
        />
      ) : null}

      {sidebarOverlay ? (
        <Sheet
          open={sidebarOverlayOpen}
          onOpenChange={(open) => {
            setSidebarOverlayOpen(open);
            if (!open) restoreReturnFocus("sidebar");
          }}
        >
          <SheetContent
            side="left"
            className={cn(
              "flex h-dvh flex-col gap-0 overflow-hidden bg-card p-0 text-card-foreground",
              mobilePanel
                ? "inset-0 size-dvw !max-w-none border-0"
                : "w-[min(360px,90vw)] !max-w-none",
              panelCloseClass,
              classNames?.sidebarOverlay,
            )}
          >
            <SheetTitle className="sr-only">
              {labels?.sidebar ?? "Navigation"}
            </SheetTitle>
            <div className="min-h-0 flex-1 overflow-hidden">{sidebar}</div>
          </SheetContent>
        </Sheet>
      ) : null}
      {detailsOverlay ? (
        <Sheet
          open={detailsOverlayOpen}
          onOpenChange={(open) => {
            setDetailsOverlayOpen(open);
            if (!open) {
              restoreReturnFocus("details");
              onDetailsOpenChange?.(false);
            }
          }}
        >
          <SheetContent
            side="right"
            className={cn(
              "flex h-dvh flex-col gap-0 overflow-hidden bg-card p-0 text-card-foreground",
              mobilePanel
                ? "inset-0 size-dvw !max-w-none border-0"
                : "w-[min(520px,92vw)] !max-w-none",
              panelCloseClass,
              classNames?.detailsOverlay,
            )}
          >
            <SheetTitle className="sr-only">
              {labels?.details ?? "Details"}
            </SheetTitle>
            <div className="min-h-0 flex-1 overflow-hidden">{details}</div>
          </SheetContent>
        </Sheet>
      ) : null}
    </div>
  );
}
